import axios from '../../../axios';
import React from 'react';
import { useAuth } from '../../../context/AuthContext';

import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/system';
import {
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { toUpper } from 'lodash';

const ActivitiesPage = () => {
  interface Filters {
    type: string;
    channel: string;
    startDate: string | null;
    endDate: string | null;
  }

  const [filters, setFilters] = React.useState<Filters>({
    type: 'All',
    channel: 'All',
    startDate: null,
    endDate: null,
  });

  const [activities, setActivities] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(null);
  const [pagination, setPagination] = React.useState({
    limit: 10,
    page: 0,
  });

  const [sortField, setSortField] = React.useState('id:desc');

  const { organization } = useAuth();
  const { t } = useTranslation();

  const fetchActivities = React.useCallback(async () => {
    const params = {
      pagination: {
        enabled: 1,
        limit: pagination?.limit,
        page: pagination?.page + 1,
        sort: sortField,
      },
    } as any;

    if (filters?.type !== 'All') {
      params.filters = params.filters || {};
      params.filters.type = filters.type;
    }

    if (filters?.channel !== 'All') {
      params.filters = params.filters || {};
      params.filters.channel = filters.channel;
    }

    if (filters?.startDate) {
      params.filters = params.filters || {};
      params.filters.date__$gte = filters.startDate;
    }

    if (filters?.endDate) {
      params.filters = params.filters || {};
      params.filters.date__$lte = filters.endDate;
    }

    const { data: { data: { items = [], total_count = 0 } = {} } = {} } =
      await axios.get(
        `api/organizations/${organization?.id}/customer-activities`,
        {
          params,
        }
      );

    setActivities(items);

    if (totalCount !== total_count) {
      setTotalCount(total_count);
    }
  }, [organization, pagination, totalCount, filters, sortField]);

  const columns: GridColDef<(typeof activities)[number]>[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'customer.full_name',
      headerName: t(`CUSTOMER.TITLE`),
      width: 150,
      editable: false,
      sortable: false,
      valueGetter: (_value, row: any) => {
        return row.customer?.full_name;
      },
    },
    {
      field: 'customer.autorized_person_name',
      headerName: t(`CUSTOMERS.CUSTOMER_AUTHORIZED_PERSON_NAME`),
      width: 150,
      editable: true,
      sortable: false,
      valueGetter: (_value, row: any) => {
        return row.customer?.authorized_person_name;
      },
    },
    {
      field: 'channel',
      headerName: t(`CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_CHANNEL`),
      width: 150,
      editable: false,

      valueGetter: (_value, row: any) => {
        const activityChannel = t(
          `SYSTEM_ACTIVITY_CHANNEL_${toUpper(row.channel)}`
        );
        return activityChannel;
      },
    },
    {
      field: 'date',
      headerName: t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_DATE'),
      width: 150,
      editable: false,
    },
    {
      field: 'type',
      headerName: t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_TYPE'),
      width: 130,
      editable: false,
      valueGetter: (_value, row: any) => {
        const activityType = t(
          `SYSTEM_CUSTOMER_ACTIVITY_TYPES_${toUpper(row.type)}`
        );
        return activityType;
      },
    },
    {
      field: 'note',
      headerName: t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_NOTE'),
      sortable: false,
      width: 300,
    },
  ];

  React.useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <Box>
      <FormControl sx={{ width: 200 }}>
        <InputLabel id="filters-type-label">
          {t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_TYPE')}
        </InputLabel>
        <Select
          labelId="filters-type-label"
          id="filters-type-select"
          value={filters?.type}
          label={t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_TYPE')}
          onChange={(e) => {
            setFilters({
              ...filters,
              type: e.target.value,
            });
          }}
        >
          <MenuItem value="All">{t(`FILTERS.ALL`)}</MenuItem>
          <MenuItem value="demo_request">
            {t(`SYSTEM_CUSTOMER_ACTIVITY_TYPES_DEMO_REQUEST`)}
          </MenuItem>
          <MenuItem value="meeting_request">
            {t(`SYSTEM_CUSTOMER_ACTIVITY_TYPES_MEETING_REQUEST`)}
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl sx={{ width: 200, ml: 2 }}>
        <InputLabel id="channel-type-label">
          {t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_CHANNEL')}
        </InputLabel>
        <Select
          labelId="channel-type-label"
          id="channel-type-select"
          value={filters?.channel}
          label={t('CUSTOMERS.CUSTOMER_DETAILS.ACTIVITY_CHANNEL')}
          onChange={(e) => {
            setFilters({
              ...filters,
              channel: e.target.value,
            });
          }}
        >
          <MenuItem value="All">{t(`FILTERS.ALL`)}</MenuItem>
          <MenuItem value="phone">
            {t(`SYSTEM_ACTIVITY_CHANNEL_PHONE`)}
          </MenuItem>
          <MenuItem value="email">
            {t(`SYSTEM_ACTIVITY_CHANNEL_EMAIL`)}
          </MenuItem>
          <MenuItem value="meeting">
            {t(`SYSTEM_ACTIVITY_CHANNEL_MEETING`)}
          </MenuItem>
          <MenuItem value="online_meeting">
            {t(`SYSTEM_ACTIVITY_CHANNEL_ONLINE_MEETING`)}
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl sx={{ width: 200, ml: 2 }}>
        <DatePicker
          value={filters?.startDate}
          onChange={(date) => setFilters({ ...filters, startDate: date })}
          label={t('FILTERS.START_DATE')}
          renderInput={(params) => <TextField {...params} />}
        />
      </FormControl>

      <FormControl sx={{ width: 200, ml: 2 }}>
        <DatePicker
          value={filters?.endDate}
          onChange={(date) => setFilters({ ...filters, endDate: date })}
          label={t('FILTERS.END_DATE')}
          renderInput={(params) => <TextField {...params} />}
        />
      </FormControl>

      <DataGrid
        rows={activities}
        columns={columns}
        paginationModel={{
          pageSize: pagination?.limit,
          page: pagination?.page,
        }}
        onPaginationModelChange={(params) => {
          if (params.pageSize !== pagination.limit) {
            params.page = 0;
          }

          setPagination({
            limit: params.pageSize,
            page: params.page,
          });
        }}
        sortingMode="server"
        onSortModelChange={([sortObj]) => {
          setSortField(`${sortObj?.field}:${sortObj?.sort}`);
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
        rowCount={totalCount || 0}
        paginationMode="server"
        sx={{ minHeight: 500 }}
      />
    </Box>
  );
};

export default ActivitiesPage;
